import { ClosedEndedStatement } from 'types/interface';
import { PieChart, Pie, PieSectorShapeProps, Sector } from 'recharts';

interface Props {
   investments: ClosedEndedStatement[];
   dataType:
      | 'totalCommitment'
      | 'capitalCalled'
      | 'remainingCommitment'
      | 'distributed'
      | 'nav';
   title: string;
   colors: string[];
}

type PieLabelProps = {
   cx?: number;
   cy?: number;
   midAngle?: number;
   innerRadius?: number;
   outerRadius?: number;
   percent?: number;
   name?: string;
};

const OUTER_LABEL_MAX_LINE_LENGTH = 12;

function wrapLabel(value: string, maxLineLength: number) {
   const words = value.split(' ');
   const lines: string[] = [];
   let currentLine = '';

   words.forEach(word => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;

      if (nextLine.length <= maxLineLength || !currentLine) {
         currentLine = nextLine;
      } else {
         lines.push(currentLine);
         currentLine = word;
      }
   });

   if (currentLine) {
      lines.push(currentLine);
   }

   return lines;
}

export default function InvestmentsExposure({
   investments,
   dataType,
   title,
   colors,
}: Props) {
   const renderLabel = ({
      cx = 0,
      cy = 0,
      midAngle = 0,
      innerRadius = 0,
      outerRadius = 0,
      percent = 0,
      name = '',
   }: PieLabelProps) => {
      if (percent <= 0) {
         return null;
      }

      const angle = (-midAngle * Math.PI) / 180;
      const nameLines = wrapLabel(name, OUTER_LABEL_MAX_LINE_LENGTH);
      const percentRadius = innerRadius + (outerRadius - innerRadius) * 0.58;
      const percentX = cx + percentRadius * Math.cos(angle);
      const percentY = cy + percentRadius * Math.sin(angle);
      const connectorStartRadius = outerRadius + 4;
      const connectorBendRadius = outerRadius + 16;
      const connectorStartX = cx + connectorStartRadius * Math.cos(angle);
      const connectorStartY = cy + connectorStartRadius * Math.sin(angle);
      const connectorBendX = cx + connectorBendRadius * Math.cos(angle);
      const connectorBendY = cy + connectorBendRadius * Math.sin(angle);
      const isRightSide = Math.cos(angle) >= 0;
      const labelX = connectorBendX + (isRightSide ? 10 : -10);
      const labelY = connectorBendY;
      const textAnchor = isRightSide ? 'start' : 'end';

      return (
         <g>
            <text
               x={percentX}
               y={percentY}
               fill="#fff"
               textAnchor="middle"
               dominantBaseline="central"
               fontSize={11}
               fontWeight={600}
            >
               {`${Math.round(percent * 100)}%`}
            </text>
            <path
               d={`M ${connectorStartX} ${connectorStartY} L ${connectorBendX} ${connectorBendY} L ${labelX} ${labelY}`}
               fill="none"
               stroke="#9ca3af"
               strokeWidth={1}
            />
            <text
               x={labelX}
               y={labelY}
               fill="#4b5563"
               textAnchor={textAnchor}
               dominantBaseline="middle"
               fontSize={11}
               fontWeight={500}
            >
               {nameLines.map((line, index) => (
                  <tspan
                     key={`${name}-${index}`}
                     x={labelX}
                     dy={index === 0 ? 0 : 13}
                  >
                     {line}
                  </tspan>
               ))}
            </text>
         </g>
      );
   };

   const PieFill = (props: PieSectorShapeProps) => {
      return <Sector {...props} fill={colors[props.index % colors.length]} />;
   };

   const totalData = investments.reduce((acc, curr) => acc + curr[dataType], 0);
   const chartData = investments.map(investment => ({
      value: investment.account.fund.name,
      percent: totalData > 0 ? investment[dataType] / totalData : 0,
   }));

   return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
         <div className="px-6 py-4 text-lg font-medium text-gray-500 text-xs uppercase bg-gray-50 border-b border-gray-200">
            {title}
         </div>
         <PieChart
            style={{
               width: '100%',
               maxWidth: '460px',
               maxHeight: '70vh',
               aspectRatio: 1,
            }}
            responsive
         >
            <Pie
               cx="50%"
               cy="50%"
               data={chartData}
               dataKey="percent"
               label={renderLabel}
               labelLine={false}
               maxRadius={0}
               nameKey="value"
               outerRadius={72}
               tooltipType="none"
               shape={PieFill}
            />
         </PieChart>
      </div>
   );
}
